import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Calendar, Droplet, Disc, Shield, Check } from 'lucide-react';
import { toast } from 'sonner';

const MaintenanceCalendar = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const res = await axios.get('/vehicles');
      setVehicles(res.data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkMaintenance = async (vehicleId, type) => {
    try {
      await axios.post(`/vehicles/${vehicleId}/maintenance`, {
        [type]: true
      });
      toast.success('Wartung eingetragen');
      loadVehicles();
    } catch (error) {
      console.error('Error marking maintenance:', error);
      toast.error('Fehler beim Eintragen');
    }
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const days = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getDaysSince = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const days = Math.ceil((now - date) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getMaintenanceStatus = (days, isUntil = true) => {
    if (days === null) return { color: 'secondary', text: 'Nicht erfasst', icon: AlertTriangle };
    
    if (isUntil) {
      if (days < 0) return { color: 'destructive', text: 'Abgelaufen', icon: AlertTriangle };
      if (days < 7) return { color: 'destructive', text: `${days} Tage`, icon: AlertTriangle };
      if (days < 30) return { color: 'secondary', text: `${days} Tage`, icon: Calendar };
      return { color: 'default', text: `${days} Tage`, icon: Check };
    } else {
      if (days > 180) return { color: 'destructive', text: `Vor ${days} Tagen`, icon: AlertTriangle };
      if (days > 90) return { color: 'secondary', text: `Vor ${days} Tagen`, icon: Calendar };
      return { color: 'default', text: `Vor ${days} Tagen`, icon: Check };
    }
  };

  // Calculate upcoming maintenance
  const getUpcomingMaintenance = () => {
    const items = [];
    
    vehicles.forEach(vehicle => {
      // TÜV
      const tuvDays = getDaysUntil(vehicle.tuv_expiry);
      if (tuvDays !== null && tuvDays < 60) {
        items.push({
          vehicle: vehicle.license_plate,
          type: 'TÜV',
          days: tuvDays,
          date: vehicle.tuv_expiry,
          urgent: tuvDays < 30,
          icon: Shield
        });
      }
      
      // Ölwechsel (alle 180 Tage)
      if (vehicle.last_oil_change) {
        const daysSince = getDaysSince(vehicle.last_oil_change);
        if (daysSince > 150) {
          items.push({
            vehicle: vehicle.license_plate,
            type: 'Ölwechsel',
            days: 180 - daysSince,
            date: vehicle.last_oil_change,
            urgent: daysSince > 180,
            icon: Droplet
          });
        }
      }
      
      // Bremsen (alle 365 Tage)
      if (vehicle.last_brake_check) {
        const daysSince = getDaysSince(vehicle.last_brake_check);
        if (daysSince > 300) {
          items.push({
            vehicle: vehicle.license_plate,
            type: 'Bremsenprüfung',
            days: 365 - daysSince,
            date: vehicle.last_brake_check,
            urgent: daysSince > 365,
            icon: Disc
          });
        }
      }
    });
    
    return items.sort((a, b) => {
      if (a.urgent && !b.urgent) return -1;
      if (!a.urgent && b.urgent) return 1;
      return a.days - b.days;
    });
  };

  const upcomingMaintenance = getUpcomingMaintenance();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="maintenance-calendar">
      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
          Wartungskalender
        </h1>
        <p className="text-zinc-400">TÜV, Ölwechsel und Bremsenprüfungen im Überblick</p>
      </div>

      {/* Urgent Alerts */}
      {upcomingMaintenance.filter(m => m.urgent).length > 0 && (
        <Card className="bg-red-950/30 border-red-800">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <CardTitle className="text-xl font-bold text-red-100">
                Dringende Wartungen ({upcomingMaintenance.filter(m => m.urgent).length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingMaintenance.filter(m => m.urgent).map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center justify-between p-4 bg-red-900/30 rounded-lg border border-red-800">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-red-900 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-red-300" />
                      </div>
                      <div>
                        <div className="font-bold text-red-100">{item.vehicle}</div>
                        <div className="text-sm text-red-300">{item.type}</div>
                      </div>
                    </div>
                    <Badge variant="destructive" className="text-sm">
                      {item.days < 0 ? 'Überfällig' : `${item.days} Tage`}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Vehicles Maintenance Overview */}
      <div className="grid grid-cols-1 gap-6">
        {vehicles.map(vehicle => {
          const tuvDays = getDaysUntil(vehicle.tuv_expiry);
          const oilDays = getDaysSince(vehicle.last_oil_change);
          const brakeDays = getDaysSince(vehicle.last_brake_check);
          
          const tuvStatus = getMaintenanceStatus(tuvDays, true);
          const oilStatus = getMaintenanceStatus(oilDays, false);
          const brakeStatus = getMaintenanceStatus(brakeDays, false);
          
          return (
            <Card key={vehicle.vehicle_id} className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl font-black text-white">{vehicle.license_plate}</CardTitle>
                    <p className="text-sm text-zinc-400">{vehicle.model}</p>
                  </div>
                  <Badge 
                    variant={vehicle.status === 'maintenance' ? 'destructive' : 'secondary'}
                    className="capitalize"
                  >
                    {vehicle.status === 'in_use' ? 'Im Einsatz' : vehicle.status === 'maintenance' ? 'Wartung' : 'Verfügbar'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* TÜV */}
                  <div className="p-4 bg-zinc-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className={`h-5 w-5 ${tuvDays < 30 ? 'text-red-400' : 'text-zinc-400'}`} />
                      <div className="font-bold text-white">TÜV</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm text-zinc-400">Ablauf</div>
                      <Badge variant={tuvStatus.color}>
                        {tuvStatus.text}
                      </Badge>
                      <div className="text-xs text-zinc-500 mt-2">
                        {vehicle.tuv_expiry && new Date(vehicle.tuv_expiry).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                  </div>
                  
                  {/* Ölwechsel */}
                  <div className="p-4 bg-zinc-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Droplet className={`h-5 w-5 ${oilDays > 180 ? 'text-amber-400' : 'text-zinc-400'}`} />
                      <div className="font-bold text-white">Ölwechsel</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm text-zinc-400">Letzter Wechsel</div>
                      <Badge variant={oilStatus.color}>
                        {oilStatus.text}
                      </Badge>
                      {vehicle.last_oil_change && (
                        <div className="text-xs text-zinc-500 mt-2">
                          {new Date(vehicle.last_oil_change).toLocaleDateString('de-DE')}
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full mt-2 border-zinc-700"
                        onClick={() => handleMarkMaintenance(vehicle.vehicle_id, 'oil_change')}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Erledigt
                      </Button>
                    </div>
                  </div>
                  
                  {/* Bremsen */}
                  <div className="p-4 bg-zinc-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Disc className={`h-5 w-5 ${brakeDays > 365 ? 'text-amber-400' : 'text-zinc-400'}`} />
                      <div className="font-bold text-white">Bremsen</div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm text-zinc-400">Letzte Prüfung</div>
                      <Badge variant={brakeStatus.color}>
                        {brakeStatus.text}
                      </Badge>
                      {vehicle.last_brake_check && (
                        <div className="text-xs text-zinc-500 mt-2">
                          {new Date(vehicle.last_brake_check).toLocaleDateString('de-DE')}
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full mt-2 border-zinc-700"
                        onClick={() => handleMarkMaintenance(vehicle.vehicle_id, 'brake_check')}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Erledigt
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MaintenanceCalendar;
